import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '../store/auth.store';
import { GuestFlowStore } from '../services/guest-flow.store';
import { ROUTES } from '../routes';
import { waitForAuthHydration } from './wait-for-auth-hydration';

/**
 * Angular functional-guard equivalent of Consumer's core/components/OnboardingRoute.tsx.
 * Consumer's guard only checks that *some* identity (subscriber id / guest
 * token) exists — actual step-skip prevention happens per-screen via
 * sessionStorage checks (see documents/technical/02-registration-onboarding.md
 * section 6). Replicate that same two-layer approach when porting each screen.
 *
 * Also enforces the splash-back-lock (see GuestFlowStore doc comment) —
 * Angular's guard-redirects-before-activation model replaces Consumer's
 * popstate-based GuestFlowLocationGuard for this same protection.
 *
 * Waits for bootstrap hydration to finish (see `wait-for-auth-hydration.ts`)
 * before reading `guestToken`/`accessToken` — both are only restored from
 * storage once hydration completes, so deciding beforehand risked reading
 * their default (empty) values and bouncing back to Splash unnecessarily.
 */
export const onboardingGuard: CanActivateFn = async () => {
  const authStore = inject(AuthStore);
  const guestFlowStore = inject(GuestFlowStore);
  const router = inject(Router);

  await waitForAuthHydration(authStore);

  // Mirrors guest.guard.ts's own check: an already-authenticated user must
  // never be able to re-enter onboarding/registration via browser-back after
  // login — without this, popping history back onto an onboarding route
  // bypassed the splash-lock entirely (the lock is cleared on login success,
  // so `isSplashBackLocked()` alone can't catch this case).
  if (authStore.isAuthenticated()) {
    return router.createUrlTree([`/${ROUTES.DASHBOARD}`]);
  }

  if (guestFlowStore.isSplashBackLocked()) {
    return router.createUrlTree([`/${ROUTES.LOGIN}`]);
  }

  const hasIdentity = !!authStore.guestToken() || !!authStore.accessToken();
  if (hasIdentity) return true;

  return router.createUrlTree([`/${ROUTES.LOGIN}`]);
};
